import { router, usePage } from "@inertiajs/react"
import { Bell } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { NotificationsPanel } from "@/components/notifications/notifications-panel"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import type { NotificationRow } from "@/types/notifications"

const BADGE_OVERFLOW = 9
const PANEL_RECENT_PROP = "recent_notifications"

interface BellTriggerProps {
  className?: string
  variant?: "icon" | "row"
}

/**
 * OKS-36 — Bell trigger + dropdown panel.
 *
 * Reads `unreadNotificationsCount` from the OKS-5 shared-props
 * context. Picks Popover (≥768px) or Sheet (<768px) via
 * `useIsMobile()` per UX Spec §5. Panel data (`recent_notifications`)
 * is fetched lazily on first open via Inertia partial reload — no
 * eager round-trip on every page render.
 *
 * Accessible label includes the unread count (UX Spec §6 — count must
 * be in the label, not just visual). Uses ICU plural via i18next so
 * "0 unread" / "1 unread" / "N unread" all read naturally in EN+FR.
 *
 * OKS-598 — `variant` picks the trigger layout:
 *   - `"icon"` (default): the 36px icon-only bell used inline at ≥sm.
 *   - `"row"`: the trigger becomes a full-width, ≥44px menu row (icon +
 *     label) for the collapsed "…" overflow menu below sm. The row ITSELF
 *     is the Sheet/Popover trigger — the panel still opens in place (no
 *     nested button, no navigation). `aria-haspopup`/`aria-expanded` and
 *     the count-aware `aria-label` are preserved.
 */
export function BellTrigger({ className, variant = "icon" }: BellTriggerProps) {
  const { t } = useTranslation()
  const { unreadNotificationsCount } = useCurrentContext()
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)

  const { props } = usePage<{
    [PANEL_RECENT_PROP]?: NotificationRow[] | null
  }>()
  const recent = useMemo<NotificationRow[] | null>(
    () => props[PANEL_RECENT_PROP] ?? null,
    [props],
  )

  // Fetch the panel's recent rows on first open. Subsequent opens
  // reuse the props (Inertia keeps them across visits). Mark-all-read
  // also re-resolves them via the next page reload.
  useEffect(() => {
    if (!open || hasFetched) return
    router.reload({
      only: [PANEL_RECENT_PROP],
      preserveUrl: true,
      onSuccess: () => setHasFetched(true),
    })
  }, [open, hasFetched])

  const ariaLabel = useMemo(() => {
    if (unreadNotificationsCount === 0)
      return t("notifications.bell.label_zero")
    if (unreadNotificationsCount === 1) return t("notifications.bell.label_one")
    return t("notifications.bell.label_other", {
      count: unreadNotificationsCount,
    })
  }, [t, unreadNotificationsCount])

  const isRow = variant === "row"
  const trigger = (
    <Button
      type="button"
      variant="ghost"
      size={isRow ? "default" : "icon"}
      data-testid="notifications-bell-trigger"
      aria-label={ariaLabel}
      aria-haspopup="dialog"
      aria-expanded={open}
      className={cn(
        "relative",
        isRow ? "h-11 w-full justify-start gap-2 px-2" : "size-9",
        className,
      )}
    >
      <Bell aria-hidden="true" className="size-5" />
      {isRow && (
        <span className="text-sm font-medium">
          {t("notifications.panel.title")}
        </span>
      )}
      <BellBadge count={unreadNotificationsCount} />
    </Button>
  )

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger render={trigger} />
        <SheetContent
          side="right"
          className="flex w-full flex-col p-0 sm:max-w-md"
          data-testid="notifications-sheet"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{t("notifications.panel.title")}</SheetTitle>
          </SheetHeader>
          <NotificationsPanel
            notifications={recent}
            unreadCount={unreadNotificationsCount}
            isLoading={!hasFetched && recent === null}
            onRowMarkRead={() => setOpen(false)}
            className="flex-1"
          />
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={trigger} />
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[400px] p-0"
        data-testid="notifications-popover"
      >
        <NotificationsPanel
          notifications={recent}
          unreadCount={unreadNotificationsCount}
          isLoading={!hasFetched && recent === null}
          onRowMarkRead={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  )
}

/**
 * Red dot + count pill in the top-right of the bell icon.
 *
 * Hidden when count === 0 (UX Spec §3 — Empty state on the bell is
 * "no badge"). Caps at "9+" — the BE caps at 99 anyway, so the FE
 * cap is a defensive duplicate. Uses `aria-hidden` since the
 * accessible count is on the parent button's `aria-label`.
 */
function BellBadge({ count }: { count: number }) {
  const { t } = useTranslation()
  if (count <= 0) return null
  const display =
    count > BADGE_OVERFLOW
      ? t("notifications.bell.badge_overflow")
      : String(count)
  return (
    <span
      aria-hidden="true"
      data-testid="notifications-bell-badge"
      data-count={count}
      className={cn(
        "bg-destructive absolute top-0 right-0 text-white",
        "flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-semibold",
      )}
    >
      {display}
    </span>
  )
}
